import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { submitCorporateForm } from '../../../store/Corporate/CorporateAction';

const LeadCaptureForm = () => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    companyName: '',
    fullName: '',
    phoneNo: '',
    companyEmail: '',
    services: []
  });

  // State for UTM parameters
  const [utmParams, setUtmParams] = useState({
    source: '',
    medium: '',
    campaign: ''
  });

  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // Check if we just came from a successful submission
    const params = new URLSearchParams(window.location.search);
    if (params.get('status') === 'success') {
      setIsSuccess(true);
    }

    // Capture UTM params from URL on mount
    setUtmParams({
      source: params.get('utm_source') || 'Direct',
      medium: params.get('utm_medium') || 'Organic',
      campaign: params.get('utm_campaign') || 'None'
    });
  }, []);

  const availableServices = [
    'Employee transportation',
    'On-demand Service',
    'Outstation Biz Rides',
    'Customisable Plan'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (service) => {
    setFormData(prev => {
      const isSelected = prev.services.includes(service);
      if (isSelected) {
        return { ...prev, services: prev.services.filter(s => s !== service) };
      } else {
        return { ...prev, services: [...prev.services, service] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Map to live backend structure
    const submitData = {
      firstName: formData.fullName,
      name: formData.fullName,
      phone: formData.phoneNo,
      email: formData.companyEmail,
      company: formData.companyName,
      location: "",
      employeeVolume: "",
      requirements: "",
      services: formData.services,
    };


    try {
      // Connect to Redux Thunk and unwrap the resulting promise to trigger try/catch
      await dispatch(submitCorporateForm(submitData)).unwrap();

      // Trigger our beautiful success overlay instead of a toast
      setIsSuccess(true);
      
      // Update URL for tracking purposes
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('status', 'success');
      window.history.replaceState({}, '', currentUrl);

    } catch (error) {
      console.error('Error submitting form via Redux:', error);
      alert(`Submission Error: ${error || 'Please try again later.'}`);
    }
  };

  if (isSuccess) {
    return (
      <div id="contact" className="glass-primary p-[32px] rounded-[12px] shadow-2xl max-w-[448px] mx-auto md:ml-auto border border-white/20 text-center py-[64px]">
        <div className="mb-[24px]">
          <span className="material-symbols-outlined text-[60px] text-white">check_circle</span>
        </div>
        <h3 className="font-headline text-[24px] font-bold text-on-primary mb-[8px]">Thank You!</h3>
        <p className="text-white/80">Your interest has been recorded. Our team will contact you shortly with tailored pricing.</p>
        <button 
          onClick={() => {
            setIsSuccess(false)
            setFormData({
              companyName: '',
              fullName: '',
              phoneNo: '',
              companyEmail: '',
              services: []
            })
          }}
          className="mt-[32px] text-[14px] font-semibold text-white underline underline-offset-4 decoration-white/40"
        >
          Submit another response
        </button>
      </div>
    );
  }

  return (
    <div id="contact" className="glass-primary p-[32px] rounded-[12px] shadow-2xl max-w-[448px] mx-auto md:ml-auto border border-white/20">
      <h3 className="font-headline text-[24px] font-bold text-on-primary mb-[16px] leading-tight">
        Your journey to<br />
        effortless <em className="not-italic font-extrabold underline underline-offset-4 decoration-white/40">MOBILITY.</em>
      </h3>
      <form className="space-y-[16px]" onSubmit={handleSubmit}>
        <div>
          <label className="block text-[12px] font-semibold text-on-primary/80 mb-[4px] uppercase tracking-wider">Full Name</label>
          <input
            className="w-full px-[16px] py-[10px] rounded-[8px] bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-[14px]"
            placeholder="John Doe"
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-on-primary/80 mb-[4px] uppercase tracking-wider">Company Name</label>
          <input
            className="w-full px-[16px] py-[10px] rounded-[8px] bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-[14px]"
            placeholder="Enter company name"
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleInputChange}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-[16px]">
          <div>
            <label className="block text-[12px] font-semibold text-on-primary/80 mb-[4px] uppercase tracking-wider">Phone No.</label>
            <input
              className="w-full px-[16px] py-[10px] rounded-[8px] bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-[14px]"
              placeholder="+91 00000 00000"
              type="tel"
              name="phoneNo"
              value={formData.phoneNo}
              onChange={handleInputChange}
              required
            />
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-on-primary/80 mb-[4px] uppercase tracking-wider">Work Email</label>
            <input
              className="w-full px-[16px] py-[10px] rounded-[8px] bg-white/10 border border-white/20 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all text-[14px]"
              placeholder="name@company.com"
              type="email"
              name="companyEmail"
              value={formData.companyEmail}
              onChange={handleInputChange}
              required
            />
          </div>
        </div>

        <div className="space-y-[8px]">
          <label className="block text-[12px] font-semibold text-on-primary/80 mb-[4px] uppercase tracking-wider">Services Required</label>
          <div className="grid grid-cols-2 gap-[8px]">
            {availableServices.map((service) => (
              <label key={service} className="flex items-center gap-[12px] cursor-pointer group">
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    className="peer h-[20px] w-[20px] cursor-pointer appearance-none rounded-[4px] border border-white/30 bg-white/10 checked:bg-white transition-all focus:outline-none"
                    checked={formData.services.includes(service)}
                    onChange={() => handleServiceChange(service)}
                  />
                  <span className="material-symbols-outlined absolute text-primary opacity-0 peer-checked:opacity-100 text-[14px] pointer-events-none left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-bold">
                    check
                  </span>
                </div>
                <span className="text-[14px] text-white/90 group-hover:text-white transition-colors">
                  {service}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-center mt-[32px] mb-[40px]">
          <button
            type="submit"
            className="fancy"
          >
            <span className="top-key"></span>
            <span className="bottom-key-1"></span>
            <span className="bottom-key-2"></span>
            <span className="text">Get Corporate Pricing</span>
          </button>
        </div>
      </form>
      <div className="pt-[24px] border-t border-white/10 mt-[8px]">
        <p className="text-[10px] text-white/60 text-center leading-tight">
          By submitting, you agree to our privacy policy and terms for executive mobility services.
        </p>
      </div>
    </div>
  );
};

export default LeadCaptureForm;


